import { useCallback, useState } from "react";

import { fromJson, toQueryString, withData } from "./client/helpers";

import type { HelpMetadataModel } from "@bokio/shared/models/HelpMetadataModel";
import type { HelpSearchResultModel } from "@bokio/shared/models/HelpSearchResultModel";
import type { UmbracoReleaseNotesModel } from "@bokio/shared/models/UmbracoReleaseNotesModel";

class HttpClient {
	constructor(public baseUrl: string) {}

	public get<T>(path: string, data: Record<string, unknown> = {}): Promise<T> {
		let fullPath = path;
		if (Object.keys(data).length > 0) {
			fullPath += "?" + toQueryString(data);
		}
		return this.request<T>("GET", fullPath);
	}

	public request<T>(method: string, path: string, data: Record<string, unknown> = {}, isFormData = false): Promise<T> {
		const options = withData(method, data, isFormData);

		// make IE stop caching stuff like crazy
		options.headers["If-Modified-Since"] = "Mon, 26 Jul 1997 05:00:00 GMT";
		// extra
		options.headers["Cache-Control"] = "no-cache";
		options.headers.Pragma = "no-cache";

		return fetch(this.baseUrl + path, options).then(fromJson);
	}
}

const createClient = () => {
	const umbracoBaseUrl = window.config.websiteUrl;
	const client = new HttpClient(umbracoBaseUrl);
	return client;
};

const helpMetadataRequestCache: Record<string, Promise<HelpMetadataModel>> = {};
let helpMetadataCache: Record<string, HelpMetadataModel> = {};

export const useUmbracoApi = () => {
	const [preloadedHelpMetadata, setPreloadedHelpMetadata] = useState(helpMetadataCache);

	const search = (searchString: string, maxCount: number): Promise<HelpSearchResultModel> => {
		return createClient().get<HelpSearchResultModel>(
			`umbraco/surface/FaqListSurface/Search?q=${searchString}&maxCount=${maxCount}`,
		);
	};

	const getHelpCacheId = (pageId: string) => `${window.config.websiteUrl}/${pageId}`;

	const getHelp = async (pageId: string): Promise<HelpMetadataModel> => {
		const cacheId = getHelpCacheId(pageId);
		const existing = helpMetadataRequestCache[cacheId];

		if (existing) {
			return existing;
		}

		try {
			helpMetadataRequestCache[cacheId] = createClient().get<HelpMetadataModel>(
				`umbraco/surface/FaqListSurface/GetMetadata?id=${pageId}`,
			);
			helpMetadataCache[cacheId] = await helpMetadataRequestCache[cacheId];
			helpMetadataCache = { ...helpMetadataCache };
			setPreloadedHelpMetadata(helpMetadataCache);
		} catch {
			delete helpMetadataRequestCache[cacheId];
			delete helpMetadataCache[cacheId];
		}

		return helpMetadataRequestCache[cacheId];
	};

	const getCachedHelpMetadata = (pageId: string): HelpMetadataModel | undefined => {
		// No need to wait here because the get will set state on success -> trigger rerender -> re-run this function -> then finally return the actual HelpMetadataModel
		getHelp(pageId);
		return preloadedHelpMetadata[getHelpCacheId(pageId)];
	};

	const getWhatsNew = useCallback(() => {
		return createClient().get<UmbracoReleaseNotesModel>("umbraco/surface/ReleaseNotesSurface/ListMetadata");
	}, []);

	const getReleaseNotesAllEmbedUrl = useCallback((): string => {
		const baseUrl = window.config.websiteUrl.replace(/\/$/, "");
		return `${baseUrl}/umbraco/surface/ReleaseNotesSurface/GetAllAsEmbed`;
	}, []);

	return { search, getHelp, getCachedHelpMetadata, getWhatsNew, getReleaseNotesAllEmbedUrl };
};
